import { useEffect, useMemo, useRef, useState } from "react";
import OverheadPitch from "./OverheadPitch";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { X, RotateCcw, Undo2, Trash2, Play, Pause, MousePointer2, Footprints, MoveUpRight, Circle, Shield } from "lucide-react";
import { assignStandardPositions, computeFrame, pathLength, simplifyPath, clamp } from "../lib/engine";

const uid = () => Math.random().toString(36).slice(2, 9);
const round1 = (n) => Math.round(n * 10) / 10;
const speedLabel = (s) => (s < 0.4 ? "Very slow" : s < 0.85 ? "Slow" : s < 1.3 ? "Normal" : "Fast");
const BASE_SPEED = 5; // m/s baseline; the slider scales real-time playback
const MARK_GAP = 6.5; // keep marker tethered a readable distance away, never on top
const TOOLS = [
  { id: "move", label: "Move", icon: MousePointer2 },
  { id: "run", label: "Run", icon: Footprints },
  { id: "ball", label: "Ball", icon: Circle },
  { id: "arrow", label: "Arrow", icon: MoveUpRight },
];

// Full-screen live tactics board: drag players, drop a ball, draw run paths, then animate the
// runs while you move other players by hand to explain a tactic. Nothing here is saved to a play.
export default function Whiteboard({ play, teamColors, jersey, onClose }) {
  const startPositions = useMemo(() => {
    const pos = assignStandardPositions(play.entities);
    return play.entities.map((e) => ({ ...e, ...(pos[e.id] || {}) }));
  }, [play.entities]);

  const [ents, setEnts] = useState(startPositions);
  const [runs, setRuns] = useState({});          // { playerId: { waypoints:[{x,y}], pace } }
  const [arrows, setArrows] = useState([]);       // { id, x1, y1, x2, y2 }
  const [ball, setBall] = useState(null);         // free ball { x, y }
  const [ballHolder, setBallHolder] = useState(null);
  const [tool, setTool] = useState("move");
  const [speed, setSpeed] = useState(0.6);
  const speedRef = useRef(0.6);
  speedRef.current = speed;
  const [oppFollow, setOppFollow] = useState(true);
  const [selId, setSelId] = useState(null);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [preview, setPreview] = useState(null);

  const hist = useRef([]);
  const drawRef = useRef(null);
  const ballDragRef = useRef(false);
  const wpDrag = useRef(false);

  const pushHist = () => {
    hist.current.push({ ents, runs, arrows, ball, ballHolder });
    if (hist.current.length > 40) hist.current.shift();
  };
  const undo = () => {
    const p = hist.current.pop();
    if (!p) return;
    setEnts(p.ents); setRuns(p.runs); setArrows(p.arrows); setBall(p.ball); setBallHolder(p.ballHolder);
    setPlaying(false); setT(0);
  };

  const runDur = (base, run) => clamp(pathLength([{ x: base.x, y: base.y }, ...run.waypoints]) / BASE_SPEED, 0.8, 12);
  const runEvents = useMemo(() => Object.entries(runs).map(([pid, r]) => {
    const e = ents.find((x) => x.id === pid);
    if (!e) return null;
    return { id: "r" + pid, kind: "move", type: "PLAYER RUN", playerId: pid, startTime: 0, endTime: runDur(e, r), waypoints: r.waypoints };
  }).filter(Boolean), [runs, ents]);
  const maxDur = useMemo(() => Math.max(0, ...runEvents.map((e) => e.endTime)), [runEvents]);

  const frame = useMemo(() => {
    const fr = computeFrame({ entities: ents, events: runEvents, ball: {} }, t);
    let ballPos = null;
    if (ballHolder) { const h = fr.entities.find((e) => e.id === ballHolder); if (h) ballPos = { x: h.x, y: h.y, z: 0.4 }; }
    else if (ball) ballPos = { x: ball.x, y: ball.y, z: 0.4 };
    return { entities: fr.entities, ball: ballPos };
  }, [ents, runEvents, t, ballHolder, ball]);

  // animation
  useEffect(() => {
    if (!playing) return;
    let last = performance.now(); let raf;
    const loop = (now) => {
      const dt = (now - last) / 1000; last = now;
      setT((prev) => { const nt = prev + dt * speedRef.current; if (nt >= maxDur) { setPlaying(false); return maxDur; } return nt; });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing, maxDur]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.code === "Space") { e.preventDefault(); togglePlay(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line

  const pairedOpp = (id, list) => {
    const home = list.find((e) => e.id === id);
    if (!home || home.team !== "A") return null;
    const paired = list.find((e) => e.team === "B" && e.number === 17 - home.number);
    if (paired) return paired;
    return list.filter((e) => e.team === "B").map((e) => ({ e, d: Math.hypot(e.x - home.x, e.y - home.y) })).sort((a, b) => a.d - b.d)[0]?.e || null;
  };

  const onMovePlayer = (id, world, isEnd) => {
    if (isEnd) {
      const d = drawRef.current; drawRef.current = null; setPreview(null);
      if (!d || !d.moved) return;
      if (d.mode === "run") {
        const base = ents.find((e) => e.id === d.id);
        const pts = simplifyPath([{ x: base.x, y: base.y }, ...d.points, d.last], 1.2).slice(1).slice(-12).map((p) => ({ x: round1(p.x), y: round1(p.y) }));
        if (pts.length) { pushHist(); setRuns((r) => ({ ...r, [d.id]: { waypoints: pts, speed } })); setT(0); setPlaying(false); }
      }
      return;
    }
    if (world == null) return;
    if (!drawRef.current) {
      if (tool === "run") drawRef.current = { mode: "run", id, points: [], last: world, moved: false };
      else {
        pushHist();
        const home = ents.find((e) => e.id === id);
        const marker = oppFollow && home && home.team === "A" ? pairedOpp(id, ents) : null;
        let off = null;
        if (marker && home) {
          const dx = marker.x - home.x, dy = marker.y - home.y;
          const len = Math.hypot(dx, dy) || 1;
          off = { ux: dx / len, uy: dy / len, gap: Math.max(len, MARK_GAP) }; // elastic tether, never on top
        }
        drawRef.current = { mode: "move", id, markerId: marker?.id || null, off, moved: false };
      }
    }
    const d = drawRef.current;
    if (d.mode === "run") {
      const lp = d.points[d.points.length - 1] || d.last;
      if (Math.hypot(world.x - lp.x, world.y - lp.y) >= 1.5) d.points.push({ x: world.x, y: world.y });
      d.last = world; d.moved = true;
      setPreview({ id, x: world.x, y: world.y, points: [...d.points, world] });
    } else {
      d.moved = true;
      setEnts((list) => list.map((e) => {
        if (e.id === id) return { ...e, x: round1(world.x), y: round1(world.y) };
        if (d.markerId && e.id === d.markerId && d.off) return { ...e, x: round1(clamp(world.x + d.off.ux * d.off.gap, 1, 89)), y: round1(clamp(world.y + d.off.uy * d.off.gap, 1, 144)) };
        return e;
      }));
    }
  };

  const onMoveWaypoint = (evId, idx, world, isEnd) => {
    if (isEnd) { wpDrag.current = false; return; }
    if (!wpDrag.current) { pushHist(); wpDrag.current = true; setT(0); setPlaying(false); }
    const pid = String(evId).slice(1);
    setRuns((r) => { const run = r[pid]; if (!run) return r; return { ...r, [pid]: { ...run, waypoints: run.waypoints.map((w, i) => (i === idx ? { x: round1(world.x), y: round1(world.y) } : w)) } }; });
  };

  const onMoveBall = (world, isEnd) => {
    if (isEnd) { ballDragRef.current = false; return; }
    if (!ballDragRef.current) { pushHist(); ballDragRef.current = true; setBallHolder(null); }
    setBall({ x: round1(world.x), y: round1(world.y) });
  };

  const onSelect = (id) => {
    if (tool === "ball") { pushHist(); setBallHolder(id); setBall(null); return; }
    setSelId(id);
  };
  const onPitchPoint = (world) => {
    if (tool === "ball") { pushHist(); setBall({ x: round1(world.x), y: round1(world.y) }); setBallHolder(null); }
  };
  const onArrowDraw = (a) => { pushHist(); setArrows((arr) => [...arr, { id: uid(), x1: round1(a.x1), y1: round1(a.y1), x2: round1(a.x2), y2: round1(a.y2) }]); };

  const togglePlay = () => {
    if (maxDur <= 0) return;
    setPlaying((p) => {
      if (!p && t >= maxDur) setT(0);
      return !p;
    });
  };
  const rewind = () => { setT(0); setPlaying(false); };
  const clearAll = () => {
    pushHist();
    setRuns({}); setArrows([]); setBall(null); setBallHolder(null); setEnts(startPositions);
    setT(0); setPlaying(false); setSelId(null);
  };

  const toolProp = tool === "move" ? "select" : tool;
  const boardPlay = { entities: ents, events: runEvents, annotations: arrows.map((a) => ({ ...a, type: "arrow" })) };
  const hasRuns = maxDur > 0;

  return (
    <div className="fixed inset-0 z-50 bg-[#08130b] flex flex-col" data-testid="whiteboard">
      {/* top controls */}
      <div className="absolute top-2 left-2 right-2 z-10 flex items-start gap-1.5 flex-wrap">
        <div className="text-[10px] md:text-xs font-semibold uppercase tracking-widest bg-background/80 backdrop-blur border border-[#F59E0B]/50 text-[#F59E0B] rounded-full px-3 py-1.5 shrink-0">Tactics board</div>
        {/* tool switch */}
        <div className="flex rounded-full border border-border bg-background/85 backdrop-blur overflow-hidden" data-testid="board-tools">
          {TOOLS.map((tl) => (
            <button key={tl.id} data-testid={`board-tool-${tl.id}`} onClick={() => setTool(tl.id)} title={tl.label}
              className={`flex items-center gap-1 text-[10px] md:text-xs uppercase tracking-wider px-2.5 py-1.5 ${tool === tl.id ? "bg-[#F59E0B] text-black font-bold" : "text-muted-foreground"}`}>
              <tl.icon className="h-3.5 w-3.5" /><span className="hidden sm:inline">{tl.label}</span>
            </button>
          ))}
        </div>
        {(tool === "run" || hasRuns) && (
          <div className="flex items-center gap-2 rounded-full border border-border bg-background/85 backdrop-blur px-3 py-1.5" data-testid="board-speed">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">Speed</span>
            <Slider data-testid="board-speed-slider" value={[speed]} min={0.15} max={2} step={0.05} onValueChange={(v) => setSpeed(v[0])} className="w-24 md:w-32" />
            <span className="text-[10px] font-bold text-[#F59E0B] whitespace-nowrap w-20 text-right">{speed.toFixed(2)}× · {speedLabel(speed)}</span>
          </div>
        )}
        {tool === "move" && (
          <button data-testid="board-opp-follow" onClick={() => setOppFollow((v) => !v)}
            className={`text-[10px] md:text-xs uppercase tracking-wider rounded-full px-3 py-1.5 border flex items-center gap-1 backdrop-blur ${oppFollow ? "bg-[#EF4444]/20 border-[#EF4444]/60 text-[#fca5a5]" : "bg-background/80 border-border text-muted-foreground"}`}>
            <Shield className="h-3 w-3" />Opposition follows {oppFollow ? "on" : "off"}
          </button>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <Button data-testid="board-undo" size="sm" variant="secondary" className="h-8" onClick={undo}><Undo2 className="h-3.5 w-3.5 md:mr-1" /><span className="hidden md:inline">Undo</span></Button>
          <Button data-testid="board-clear" size="sm" variant="secondary" className="h-8" onClick={clearAll}><Trash2 className="h-3.5 w-3.5 md:mr-1" /><span className="hidden md:inline">Clear</span></Button>
          <Button data-testid="board-close" size="sm" className="h-8 bg-[#F59E0B] hover:bg-[#d97706] text-black font-bold" onClick={onClose}><X className="h-3.5 w-3.5 md:mr-1" /><span className="hidden md:inline">Exit</span></Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 p-1 pt-14">
        <OverheadPitch
          play={boardPlay} frame={frame} tool={toolProp} selectedId={selId}
          onSelect={onSelect} onMovePlayer={onMovePlayer} onMoveWaypoint={onMoveWaypoint}
          onPitchPoint={onPitchPoint} onArrowDraw={onArrowDraw}
          ballDraggable={tool === "move" && !!frame.ball} onMoveBall={onMoveBall}
          dragPreview={preview} teamColors={teamColors} homeJersey={jersey} showPaths />
      </div>

      {/* transport */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-background/85 backdrop-blur border border-border rounded-full pl-1.5 pr-3 py-1">
        <Button data-testid="board-play" size="icon" className="h-9 w-9 rounded-full" onClick={togglePlay} disabled={!hasRuns}>
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
        </Button>
        <Button data-testid="board-rewind" size="icon" variant="ghost" className="h-8 w-8" onClick={rewind} disabled={!hasRuns}><RotateCcw className="h-4 w-4" /></Button>
        <span className="font-mono2 text-[11px] text-muted-foreground whitespace-nowrap">
          {hasRuns ? <><span className="text-[#F59E0B] font-bold">{t.toFixed(1)}s</span> / {maxDur.toFixed(1)}s</> : "Draw a run to animate"}
        </span>
      </div>

      <div className="pointer-events-none absolute bottom-2 left-3 z-10 text-[10px] uppercase tracking-widest text-white/55 hidden lg:block max-w-[26%]">
        {tool === "move" && "Drag players or the ball"}
        {tool === "run" && "Drag a player to draw their run path"}
        {tool === "ball" && "Tap a player to give them the ball · tap space to drop it"}
        {tool === "arrow" && "Drag to draw a tactical arrow"}
      </div>
    </div>
  );
}
